/**
 * PhysicalExamForm Component
 * Form for physical examination
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Box, Grid, TextField, Typography, Divider } from '@mui/material';

import { UpdateMedicalRecordData } from '../../../types/medicalRecord';

interface PhysicalExamFormProps {
  data: UpdateMedicalRecordData;
  onChange: (data: UpdateMedicalRecordData) => void;
  disabled?: boolean;
}

export const PhysicalExamForm: React.FC<PhysicalExamFormProps> = ({
  data,
  onChange,
  disabled = false,
}) => {
  const handleChange =
    (field: keyof UpdateMedicalRecordData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...data, [field]: event.target.value ?? null });
    };

  return (
    <Box>
      {/* Estado Geral */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Estado Geral
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={2}
        placeholder="BEG, corado, hidratado, acianótico, anictérico..."
        value={data.generalCondition ?? ''}
        onChange={handleChange('generalCondition')}
        disabled={disabled}
        sx={{ mb: 3 }}
      />
      <Divider sx={{ my: 2 }} />
      {/* Exame por Sistemas */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Exame Físico por Sistemas
      </Typography>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Pele e Anexos"
            placeholder="Sem lesões, cicatrizes..."
            value={data.skinExam ?? ''}
            onChange={handleChange('skinExam')}
            disabled={disabled}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Cabeça e Pescoço"
            placeholder="Sem alterações, tireoide normal..."
            value={data.headNeckExam ?? ''}
            onChange={handleChange('headNeckExam')}
            disabled={disabled}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Sistema Cardiovascular"
            placeholder="RCR, 2T, BNF, sem sopros..."
            value={data.cardiovascularExam ?? ''}
            onChange={handleChange('cardiovascularExam')}
            disabled={disabled}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Sistema Respiratório"
            placeholder="MV+, sem RA, expansibilidade preservada..."
            value={data.respiratoryExam ?? ''}
            onChange={handleChange('respiratoryExam')}
            disabled={disabled}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Abdômen"
            placeholder="Plano, RHA+, sem visceromegalias..."
            value={data.abdominalExam ?? ''}
            onChange={handleChange('abdominalExam')}
            disabled={disabled}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Sistema Neurológico"
            placeholder="Consciente, orientado, sem déficits focais..."
            value={data.neurologicalExam ?? ''}
            onChange={handleChange('neurologicalExam')}
            disabled={disabled}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Sistema Musculoesquelético"
            placeholder="Sem deformidades, amplitude de movimento preservada, força muscular grau V..."
            value={data.musculoskeletalExam ?? ''}
            onChange={handleChange('musculoskeletalExam')}
            disabled={disabled}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Outros Achados"
            placeholder="Observações adicionais do exame físico..."
            value={data.otherFindings ?? ''}
            onChange={handleChange('otherFindings')}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PhysicalExamForm;
